import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { addDays, formatDate, formatGhs } from '../../format';
import { HotelLogo } from '../../ui/HotelLogo';

/* The public booking website: what a guest sees, with no sign-in. It only talks to /public/*. */

interface Hotel {
  logoVersion: number | null;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  checkInTime: string;
  checkOutTime: string;
  today: string;
  maxNights: number;
  roomTypes: { id: string; name: string; description: string | null; maxAdults: number; maxChildren: number }[];
}
interface Offer {
  id: string;
  name: string;
  description: string | null;
  maxAdults: number;
  maxChildren: number;
  available: boolean;
  ratePlans: { id: string; name: string; total: number; perNight: number }[];
}
interface Availability {
  checkIn: string;
  checkOut: string;
  nights: number;
  roomTypes: Offer[];
}
interface Confirmation {
  reference: string;
  status: string;
  holdExpiresAt: string | null;
  total: number;
}
interface Found {
  reference: string;
  status: string;
  guestName: string;
  roomType: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  total: number;
  holdExpiresAt: string | null;
}

const STATUS_TEXT: Record<string, string> = {
  PENDING: 'Waiting for the hotel to confirm',
  CONFIRMED: 'Confirmed',
  CHECKED_IN: 'Checked in',
  CHECKED_OUT: 'Stay completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'Marked as not arrived',
};
const message = (e: unknown): string => (e instanceof ApiError ? e.message : 'We could not reach the hotel. Please check your connection and try again.');
const phoneHref = (p: string): string => `tel:${p.replace(/[^\d+]/g, '')}`;

type Step = 'search' | 'details' | 'done' | 'find';

export function BookingSite() {
  const hotel = useQuery({ queryKey: ['public', 'hotel'], queryFn: () => api<Hotel>('/public/hotel') });
  const [step, setStep] = useState<Step>('search');
  const [stay, setStay] = useState<{ checkIn: string; checkOut: string; adults: number; children: number } | null>(null);
  const [pick, setPick] = useState<{ type: Offer; planId: string } | null>(null);
  const [done, setDone] = useState<(Confirmation & { name: string; roomType: string; checkIn: string; checkOut: string }) | null>(null);

  useEffect(() => {
    if (hotel.data) document.title = `Book a room · ${hotel.data.name}`;
  }, [hotel.data]);

  const h = hotel.data;
  return (
    <div className="site">
      <header className="site-hero">
        <div className="site-wrap">
          <div className="site-brand">
            <HotelLogo version={h?.logoVersion} size={44} />
            <span>{h?.name ?? 'AA CountrySide Hotel'}</span>
          </div>
          <h1 className="site-title">Book your stay</h1>
          <p className="site-lede">{h?.address ?? 'Paga, Ghana'}. Choose your dates and send a request; we confirm it with you by phone or WhatsApp.</p>
          {h?.phone ? <a className="site-call" href={phoneHref(h.phone)}>Prefer to talk? Call {h.phone}</a> : null}
        </div>
      </header>

      <main className="site-wrap site-main">
        {hotel.isLoading ? <p role="status">Loading…</p> : null}
        {hotel.error ? <p className="site-error" role="alert">{message(hotel.error)} {hotel.error instanceof ApiError && hotel.error.status === 404 ? 'Online booking is not open yet.' : ''}</p> : null}

        {h && step === 'search' ? <SearchStep hotel={h} onPick={(s, p) => { setStay(s); setPick(p); setStep('details'); window.scrollTo({ top: 0 }); }} /> : null}
        {h && step === 'details' && stay && pick ? (
          <DetailsStep
            stay={stay}
            pick={pick}
            onBack={() => setStep('search')}
            onDone={(c, name) => { setDone({ ...c, name, roomType: pick.type.name, checkIn: stay.checkIn, checkOut: stay.checkOut }); setStep('done'); window.scrollTo({ top: 0 }); }}
          />
        ) : null}
        {h && step === 'done' && done ? <DoneStep done={done} hotel={h} onAgain={() => { setStep('search'); setDone(null); }} /> : null}
        {h && step === 'find' ? <FindStep onBack={() => setStep('search')} hotel={h} /> : null}

        {h && step !== 'find' && step !== 'done' ? (
          <p className="site-small">
            Already sent a request? <button type="button" className="site-link" onClick={() => setStep('find')}>Check its status</button>
          </p>
        ) : null}
      </main>

      <footer className="site-foot">
        <div className="site-wrap">
          <strong>{h?.name}</strong>
          {h?.address ? <span> · {h.address}</span> : null}
          {h ? <div>Check-in from {h.checkInTime} · check-out by {h.checkOutTime}. Prices include taxes.</div> : null}
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ search */
function SearchStep({ hotel, onPick }: { hotel: Hotel; onPick: (s: { checkIn: string; checkOut: string; adults: number; children: number }, p: { type: Offer; planId: string }) => void }) {
  const [checkIn, setCheckIn] = useState(addDays(hotel.today, 1));
  const [checkOut, setCheckOut] = useState(addDays(hotel.today, 3));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [asked, setAsked] = useState<{ checkIn: string; checkOut: string; adults: number; children: number } | null>(null);

  const q = useQuery({
    queryKey: ['public', 'availability', asked],
    enabled: !!asked,
    queryFn: () => api<Availability>(`/public/availability?checkIn=${asked!.checkIn}&checkOut=${asked!.checkOut}&adults=${asked!.adults}&children=${asked!.children}`),
    retry: false,
  });
  const valid = checkIn >= hotel.today && checkOut > checkIn;
  const nights = useMemo(() => Math.round((Date.parse(checkOut) - Date.parse(checkIn)) / 86_400_000), [checkIn, checkOut]);

  return (
    <>
      <form
        className="site-card site-search"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) setAsked({ checkIn, checkOut, adults, children });
        }}
      >
        <div className="site-field">
          <label htmlFor="s-in">Arrive</label>
          <input id="s-in" type="date" min={hotel.today} value={checkIn} onChange={(e) => { setCheckIn(e.target.value); if (e.target.value >= checkOut) setCheckOut(addDays(e.target.value, 1)); }} />
        </div>
        <div className="site-field">
          <label htmlFor="s-out">Leave</label>
          <input id="s-out" type="date" min={addDays(checkIn, 1)} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
        </div>
        <div className="site-field">
          <label htmlFor="s-ad">Adults</label>
          <select id="s-ad" value={adults} onChange={(e) => setAdults(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="site-field">
          <label htmlFor="s-ch">Children</label>
          <select id="s-ch" value={children} onChange={(e) => setChildren(Number(e.target.value))}>
            {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button className="site-btn" type="submit" disabled={!valid}>See rooms{valid ? ` for ${nights} night${nights === 1 ? '' : 's'}` : ''}</button>
      </form>

      {q.isFetching ? <p role="status" className="site-small">Checking what is free…</p> : null}
      {q.error ? <p className="site-error" role="alert">{message(q.error)}</p> : null}
      {q.data && !q.isFetching ? (
        q.data.roomTypes.length === 0 ? (
          <p className="site-card">We have no room that fits that many guests. Please call us and we will help.</p>
        ) : (
          <section aria-label="Rooms" className="site-rooms">
            {q.data.roomTypes.map((t) => (
              <article key={t.id} className={`site-card site-room${t.available ? '' : ' full'}`}>
                <h2>{t.name}</h2>
                {t.description ? <p>{t.description}</p> : null}
                <p className="site-small">Up to {t.maxAdults} adult{t.maxAdults === 1 ? '' : 's'}{t.maxChildren ? ` and ${t.maxChildren} child${t.maxChildren === 1 ? '' : 'ren'}` : ''}</p>
                {t.available ? (
                  t.ratePlans.map((p) => (
                    <div key={p.id} className="site-offer">
                      <div>
                        <strong>{p.name}</strong>
                        <div className="site-small">{formatGhs(p.perNight)} a night · {q.data!.nights} night{q.data!.nights === 1 ? '' : 's'}</div>
                      </div>
                      <div className="site-offer-right">
                        <div className="site-total">{formatGhs(p.total)}</div>
                        <button type="button" className="site-btn" aria-label={`Request ${t.name}, ${p.name}`} onClick={() => onPick({ checkIn: q.data!.checkIn, checkOut: q.data!.checkOut, adults: asked!.adults, children: asked!.children }, { type: t, planId: p.id })}>Request this room</button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="site-note">Not available for these dates. Try other dates, or call us.</p>
                )}
              </article>
            ))}
          </section>
        )
      ) : null}
    </>
  );
}

/* ----------------------------------------------------------------- details */
function DetailsStep({ stay, pick, onBack, onDone }: { stay: { checkIn: string; checkOut: string; adults: number; children: number }; pick: { type: Offer; planId: string }; onBack: () => void; onDone: (c: Confirmation, name: string) => void }) {
  const plan = pick.type.ratePlans.find((p) => p.id === pick.planId)!;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [company, setCompany] = useState(''); // hidden from people
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = name.trim().length >= 2 && /^\+?[0-9 ()-]{7,20}$/.test(phone.trim()) && (email.trim() === '' || /^\S+@\S+\.\S+$/.test(email.trim()));

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api<Confirmation>('/public/bookings', {
        method: 'POST',
        body: { roomTypeId: pick.type.id, ratePlanId: pick.planId, checkIn: stay.checkIn, checkOut: stay.checkOut, adults: stay.adults, children: stay.children, guest: { fullName: name.trim(), phone: phone.trim(), ...(email.trim() ? { email: email.trim() } : {}) }, ...(notes.trim() ? { notes: notes.trim() } : {}), company },
      });
      onDone(c, name.trim());
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };

  return (
    <form className="site-card site-form" onSubmit={send}>
      <button type="button" className="site-link" onClick={onBack}>← Back to rooms</button>
      <h2>Your details</h2>
      <div className="site-summary">
        <strong>{pick.type.name}</strong> · {plan.name}
        <div className="site-small">{formatDate(stay.checkIn)} → {formatDate(stay.checkOut)} · {stay.adults} adult{stay.adults === 1 ? '' : 's'}{stay.children ? `, ${stay.children} child${stay.children === 1 ? '' : 'ren'}` : ''}</div>
        <div className="site-total">{formatGhs(plan.total)} <span className="site-small">taxes included, pay at the hotel</span></div>
      </div>
      <div className="site-field">
        <label htmlFor="d-name">Full name</label>
        <input id="d-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="site-field">
        <label htmlFor="d-phone">Phone (we will call or WhatsApp this number)</label>
        <input id="d-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+233 55 000 0000" />
      </div>
      <div className="site-field">
        <label htmlFor="d-email">Email (optional)</label>
        <input id="d-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="site-field">
        <label htmlFor="d-notes">Anything we should know? (optional)</label>
        <textarea id="d-notes" rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Arrival time, a late check-in, a cot…" />
      </div>
      {/* a trap for robots: invisible and unreachable for people */}
      <div className="site-trap" aria-hidden="true">
        <label>Company<input tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} /></label>
      </div>
      {error ? <p className="site-error" role="alert">{error}</p> : null}
      <button className="site-btn" type="submit" disabled={!valid || busy}>{busy ? 'Sending…' : 'Send booking request'}</button>
      <p className="site-small">No payment is taken now. We keep the room for you for 24 hours while we confirm.</p>
    </form>
  );
}

/* -------------------------------------------------------------------- done */
function DoneStep({ done, hotel, onAgain }: { done: Confirmation & { name: string; roomType: string; checkIn: string; checkOut: string }; hotel: Hotel; onAgain: () => void }) {
  return (
    <section className="site-card site-done" aria-live="polite">
      <h2>Thank you, {done.name.split(' ')[0]}. Your request is in.</h2>
      <p>Your reference is</p>
      <p className="site-ref" aria-label="Booking reference">{done.reference}</p>
      <p>
        {done.roomType}, {formatDate(done.checkIn)} → {formatDate(done.checkOut)}, {formatGhs(done.total)} with taxes. We are holding the room for you
        {done.holdExpiresAt ? ` until ${new Date(done.holdExpiresAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}, and will contact you to confirm.
      </p>
      {hotel.phone ? <p>Questions? <a href={phoneHref(hotel.phone)}>{hotel.phone}</a></p> : null}
      <button type="button" className="site-btn" onClick={onAgain}>Make another request</button>
    </section>
  );
}

/* -------------------------------------------------------------------- find */
function FindStep({ onBack, hotel }: { onBack: () => void; hotel: Hotel }) {
  const [reference, setReference] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const look = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFound(null);
    try {
      setFound(await api<Found>('/public/bookings/lookup', { method: 'POST', body: { reference: reference.trim(), contact: contact.trim() } }));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="site-card site-form" onSubmit={look}>
      <button type="button" className="site-link" onClick={onBack}>← Back</button>
      <h2>Check a request</h2>
      <div className="site-field">
        <label htmlFor="f-ref">Reference</label>
        <input id="f-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="AA-2026-000123" autoCapitalize="characters" required />
      </div>
      <div className="site-field">
        <label htmlFor="f-contact">The phone number or email you used</label>
        <input id="f-contact" value={contact} onChange={(e) => setContact(e.target.value)} required />
      </div>
      {error ? <p className="site-error" role="alert">{error}{hotel.phone ? ` You can also call ${hotel.phone}.` : ''}</p> : null}
      <button className="site-btn" type="submit" disabled={busy || reference.trim().length < 6 || contact.trim().length < 5}>{busy ? 'Looking…' : 'Find my booking'}</button>
      {found ? (
        <div className="site-summary" role="status">
          <strong>{found.reference}</strong>: {STATUS_TEXT[found.status] ?? found.status}
          <div>{found.roomType} · {formatDate(found.checkIn)} → {formatDate(found.checkOut)} · {formatGhs(found.total)}</div>
          <div className="site-small">For {found.guestName}</div>
        </div>
      ) : null}
    </form>
  );
}
